import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";

import React, { useEffect, useMemo, useState } from "react";

import {
    Badge,
    Button,
    Container,
    Divider,
    Group,
    NumberInput,
    Paper,
    SegmentedControl,
    Select,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";

import { DatePickerInput } from "@mantine/dates";

import {
    IconCalendar,
    IconCheck,
    IconEdit,
    IconGlass,
    IconHistory,
} from "@tabler/icons-react";

import { getAuthHeaders } from "./../../api/authHelper";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

type ScopeType = "function" | "person";

type EventBar = {
    eventBarId: number;
    name: string;
    isActive: boolean;
};

type Invoice = {
    invoiceId: number;
    eventId: number;
    name: string;
    isActive: boolean;
};

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const formatDate = (date: Date | null) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const dateRange = (start: Date, end: Date) => {
    const result: string[] = [];
    const current = new Date(start);

    while (current <= end) {
        result.push(formatDate(current));
        current.setDate(current.getDate() + 1);
    }

    return result;
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const AdminDrinkAllowance: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    // -----------------------------------------------------
    // STATE
    // -----------------------------------------------------

    const [scopeType, setScopeType] =
        useState<ScopeType>("function");

    const [events, setEvents] =
        useState<any[]>([]);

    const [list, setList] =
        useState<any[]>([]);

    const [eventId, setEventId] =
        useState<number>(0);

    const [scopeId, setScopeId] =
        useState<number>(0);

    const [mode, setMode] =
        useState<"single" | "range">("single");

    const [overview, setOverview] =
        useState<any[]>([]);

    const [dateRangeValue, setDateRangeValue] =
        useState<[Date | null, Date | null]>([
            new Date(),
            new Date(),
        ]);

    const [invoices, setInvoices] =
        useState<Invoice[]>([]);

    const [invoiceId, setInvoiceId] =
        useState<number | null>(null);

    const [bars, setBars] =
        useState<EventBar[]>([]);

    const [values, setValues] =
        useState<Record<number, number>>({});

    // -----------------------------------------------------
    // HISTORY STATE
    // -----------------------------------------------------

    const [historyValues, setHistoryValues] =
        useState<Record<string, number>>({});

    const [savingHistoryKey, setSavingHistoryKey] =
        useState<string | null>(null);

    const [editingHistoryKey, setEditingHistoryKey] =
        useState<string | null>(null);

    const [saving, setSaving] =
        useState(false);

    // -----------------------------------------------------
    // LOAD EVENTS
    // -----------------------------------------------------

    useEffect(() => {
        fetch(
            `${apiUrl}/api/events/light`,
            {
                headers: getAuthHeaders(),
            }
        )
            .then((r) => r.json())
            .then((data) =>
                setEvents(
                    Array.isArray(data)
                        ? data
                        : []
                )
            )
            .catch(() =>
                setEvents([])
            );
    }, [apiUrl]);

    // -----------------------------------------------------
    // LOAD BARS
    // -----------------------------------------------------

    const loadBars = async (
        selectedEventId: number
    ) => {
        try {
            const res = await fetch(
                `${apiUrl}/api/EventBars/event/${selectedEventId}`
            );

            if (!res.ok) {
                setBars([]);
                setValues({});
                return;
            }

            const data = await res.json();

            const safe =
                Array.isArray(data)
                    ? data
                    : [];

            setBars(safe);

            const init:
                Record<number, number> = {};

            safe.forEach(
                (bar: EventBar) => {
                    init[bar.eventBarId] = 0;
                }
            );

            setValues(init);
        } catch {
            setBars([]);
            setValues({});
        }
    };

    // -----------------------------------------------------
    // LOAD INVOICES
    // -----------------------------------------------------

    const loadInvoices = async (
        selectedEventId: number
    ) => {
        try {
            const res = await fetch(
                `${apiUrl}/api/invoices/event/${selectedEventId}`
            );

            if (!res.ok) {
                setInvoices([]);
                return;
            }

            const data = await res.json();

            setInvoices(
                Array.isArray(data)
                    ? data
                    : []
            );
        } catch {
            setInvoices([]);
        }
    };

    // -----------------------------------------------------
    // LOAD PERSONS / FUNCTIONS
    // -----------------------------------------------------

    const loadScopeList = async (
        selectedEventId: number
    ) => {
        const endpoint =
            scopeType === "function"
                ? `/api/events/${selectedEventId}/functions`
                : `/api/events/${selectedEventId}/persons`;

        try {
            const res = await fetch(
                `${apiUrl}${endpoint}`,
                {
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                setList([]);
                return;
            }

            const data = await res.json();

            setList(
                Array.isArray(data)
                    ? data
                    : []
            );
        } catch {
            setList([]);
        }
    };

    // -----------------------------------------------------
    // LOAD ALLOWANCE FOR SELECTED DATE
    // -----------------------------------------------------

    const loadAllowances = async (
        selectedEventId: number,
        selectedScopeId: number,
        date: string
    ) => {
        if (
            !selectedEventId ||
            !selectedScopeId
        ) {
            return;
        }

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drinks-allowance?scopeType=${scopeType}&scopeId=${selectedScopeId}&eventId=${selectedEventId}&validDate=${date}`,
                {
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                return;
            }

            const data = await res.json();

            const newValues:
                Record<number, number> = {};

            bars.forEach((bar) => {
                newValues[
                    bar.eventBarId
                ] = 0;
            });

            let selectedInvoiceId:
                number | null = null;

            if (Array.isArray(data)) {
                data.forEach(
                    (item: any) => {
                        const barId =
                            item.eventBarId ??
                            item.barId ??
                            item.bar;

                        if (barId) {
                            newValues[
                                barId
                            ] =
                                item.maxDrinks ??
                                0;
                        }

                        if (
                            selectedInvoiceId ===
                            null &&
                            item.invoiceId != null
                        ) {
                            selectedInvoiceId =
                                item.invoiceId;
                        }
                    }
                );
            }

            setValues(newValues);
            setInvoiceId(
                selectedInvoiceId
            );
        } catch {
            // Keep existing values
        }
    };

    // -----------------------------------------------------
    // LOAD OVERVIEW
    // -----------------------------------------------------

    const loadOverview = async () => {
        if (
            !eventId ||
            !scopeId
        ) {
            setOverview([]);
            return;
        }

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drinks-allowance/overview?scopeType=${scopeType}&scopeId=${scopeId}&eventId=${eventId}`,
                {
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                setOverview([]);
                return;
            }

            const data = await res.json();

            setOverview(
                Array.isArray(data)
                    ? data
                    : []
            );
        } catch {
            setOverview([]);
        }
    };

    // -----------------------------------------------------
    // HISTORY KEY
    // -----------------------------------------------------

    const getHistoryKey = (
        day: any
    ) => {
        const barId =
            day.eventBarId ??
            day.EventBarId ??
            day.barId ??
            day.bar;

        return `${day.date}-${barId}`;
    };

    // -----------------------------------------------------
    // UPDATE HISTORY
    // -----------------------------------------------------

    const updateHistoryAllowance =
        async (
            day: any,
            newMaxDrinks: number
        ) => {
            if (
                !eventId ||
                !scopeId
            ) {
                return false;
            }

            const eventBarId =
                day.eventBarId ??
                day.EventBarId ??
                day.barId ??
                day.bar;

            if (!eventBarId) {
                alert(
                    "Could not determine the bar for this allowance."
                );

                return false;
            }

            const key =
                getHistoryKey(day);

            setSavingHistoryKey(key);

            try {
                const res = await fetch(
                    `${apiUrl}/api/admin/drinks-allowance`,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",

                            ...getAuthHeaders(),
                        },

                        body: JSON.stringify({
                            scopeType,
                            scopeId,
                            eventId,
                            eventBarId,

                            maxDrinks:
                                newMaxDrinks,

                            invoiceId:
                                day.invoiceId ??
                                invoiceId,

                            validDate:
                                day.date,
                        }),
                    }
                );

                if (!res.ok) {
                    const text =
                        await res.text();

                    throw new Error(
                        text ||
                        "Could not update drink allowance."
                    );
                }

                await loadOverview();

                const [start] =
                    dateRangeValue;

                if (start) {
                    await loadAllowances(
                        eventId,
                        scopeId,
                        formatDate(start)
                    );
                }

                setHistoryValues(
                    (prev) => {
                        const next = {
                            ...prev,
                        };

                        delete next[key];

                        return next;
                    }
                );

                return true;
            } catch (err: any) {
                alert(
                    err?.message ||
                    "Could not update drink allowance."
                );

                return false;
            } finally {
                setSavingHistoryKey(
                    null
                );
            }
        };

    // -----------------------------------------------------
    // CREATE / ASSIGN ALLOWANCES
    // -----------------------------------------------------

    const submit = async () => {
        if (
            !eventId ||
            !scopeId
        ) {
            return;
        }

        const [start, end] =
            dateRangeValue;

        if (!start) {
            return;
        }

        if (
            mode === "range" &&
            !end
        ) {
            return;
        }

        const days =
            mode === "single"
                ? [
                    formatDate(start),
                ]
                : dateRange(
                    start,
                    end!
                );

        setSaving(true);

        try {
            for (
                const day
                of days
            ) {
                for (
                    const bar
                    of bars
                ) {
                    const amount =
                        values[
                        bar.eventBarId
                        ] ?? 0;

                    const res =
                        await fetch(
                            `${apiUrl}/api/admin/drinks-allowance`,
                            {
                                method:
                                    "POST",

                                headers: {
                                    "Content-Type":
                                        "application/json",

                                    ...getAuthHeaders(),
                                },

                                body:
                                    JSON.stringify({
                                        scopeType,
                                        scopeId,
                                        eventId,

                                        eventBarId:
                                            bar.eventBarId,

                                        maxDrinks:
                                            amount,

                                        invoiceId,

                                        validDate:
                                            day,
                                    }),
                            }
                        );

                    if (!res.ok) {
                        const text =
                            await res.text();

                        alert(
                            text ||
                            "Could not save allowance."
                        );

                        return;
                    }
                }
            }

            await loadAllowances(
                eventId,
                scopeId,
                formatDate(start)
            );

            await loadOverview();

            alert(
                "Saved successfully"
            );
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------
    // EFFECT - OVERVIEW
    // -----------------------------------------------------

    useEffect(() => {
        if (
            eventId &&
            scopeId
        ) {
            loadOverview();
        } else {
            setOverview([]);
        }
    }, [
        eventId,
        scopeId,
        scopeType,
    ]);

    // -----------------------------------------------------
    // EFFECT - EVENT / SCOPE TYPE
    // -----------------------------------------------------

    useEffect(() => {
        if (!eventId) {
            setBars([]);
            setInvoices([]);
            setList([]);
            return;
        }

        loadBars(eventId);
        loadInvoices(eventId);
        loadScopeList(eventId);

    }, [
        eventId,
        scopeType,
    ]);

    // -----------------------------------------------------
    // EFFECT - DATE
    // -----------------------------------------------------

    useEffect(() => {
        const [start] =
            dateRangeValue;

        if (
            eventId &&
            scopeId &&
            start &&
            bars.length > 0
        ) {
            loadAllowances(
                eventId,
                scopeId,
                formatDate(start)
            );
        }

    }, [
        eventId,
        scopeId,
        dateRangeValue,
        scopeType,
        bars,
    ]);

    // -----------------------------------------------------
    // GROUP OVERVIEW BY DATE
    // -----------------------------------------------------

    const groupedOverview =
        useMemo(() => {
            return overview.reduce(
                (
                    groups:
                        Record<
                            string,
                            any[]
                        >,
                    item: any
                ) => {
                    const dateKey =
                        item.date;

                    if (
                        !groups[
                        dateKey
                        ]
                    ) {
                        groups[
                            dateKey
                        ] = [];
                    }

                    groups[
                        dateKey
                    ].push(item);

                    return groups;
                },
                {}
            );
        }, [overview]);

    const sortedOverviewDates =
        useMemo(() => {
            return Object.keys(
                groupedOverview
            ).sort(
                (a, b) =>
                    new Date(
                        b
                    ).getTime() -
                    new Date(
                        a
                    ).getTime()
            );
        }, [groupedOverview]);

    // -----------------------------------------------------
    // RENDER
    // -----------------------------------------------------

    return (
        <div>
            <HeroSectionV2
                titleEn="Drink Allowances"
                titleDa="Drikkevaregodtgørelse"
                descriptionEn="Manage drink allowances for persons and functions"
                descriptionDa="Administrer drikkevarer til personer og funktioner"
                imageSrc={Billede1}
            />

            <AdminBackBar
                title="Drinks"
                crumbs={[
                    {
                        label:
                            "Admin",
                        to:
                            "/admin",
                    },
                    {
                        label:
                            "Drinks",
                    },
                ]}
            />

            <Container
                size="xl"
                py="xl"
            >
                <Stack gap="xl">

                    {/* ============================================= */}
                    {/* ASSIGN DRINK ALLOWANCE */}
                    {/* ============================================= */}

                    <Paper
                        withBorder
                        radius="lg"
                        shadow="xs"
                        style={{
                            overflow:
                                "hidden",
                        }}
                    >
                        {/* HEADER */}

                        <Group p="lg">
                            <ThemeIcon
                                color="red"
                                variant="light"
                                size="lg"
                                radius="md"
                            >
                                <IconGlass
                                    size={20}
                                />
                            </ThemeIcon>

                            <div>
                                <Title order={4}>
                                    Assign drink allowance
                                </Title>

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Create or update a drink allowance
                                </Text>
                            </div>
                        </Group>

                        <Divider />

                        {/* ========================================= */}
                        {/* RECIPIENT */}
                        {/* ========================================= */}

                        <Stack
                            gap="md"
                            p="lg"
                        >
                            <div>
                                <Text
                                    fw={700}
                                    size="sm"
                                >
                                    Recipient
                                </Text>

                                <Text
                                    size="xs"
                                    c="dimmed"
                                >
                                    Select who should receive the drink allowance
                                </Text>
                            </div>

                            <SimpleGrid
                                cols={{
                                    base: 1,
                                    md: 3,
                                }}
                                spacing="lg"
                            >
                                <Select
                                    label="Recipient type"
                                    value={
                                        scopeType
                                    }
                                    onChange={(
                                        value
                                    ) => {
                                        if (
                                            !value
                                        ) {
                                            return;
                                        }

                                        setScopeType(
                                            value as
                                            ScopeType
                                        );

                                        setScopeId(
                                            0
                                        );

                                        setList(
                                            []
                                        );

                                        setOverview(
                                            []
                                        );

                                        setHistoryValues(
                                            {}
                                        );

                                        setEditingHistoryKey(
                                            null
                                        );
                                    }}
                                    data={[
                                        {
                                            value:
                                                "function",
                                            label:
                                                "Function",
                                        },
                                        {
                                            value:
                                                "person",
                                            label:
                                                "Person",
                                        },
                                    ]}
                                />

                                <Select
                                    label="Event"
                                    placeholder="Select event..."
                                    data={
                                        events.map(
                                            (
                                                event
                                            ) => ({
                                                value:
                                                    String(
                                                        event.eventID
                                                    ),

                                                label:
                                                    event.eventName,
                                            })
                                        )
                                    }
                                    value={
                                        eventId
                                            ? String(
                                                eventId
                                            )
                                            : null
                                    }
                                    onChange={(
                                        value
                                    ) => {
                                        const id =
                                            value
                                                ? Number(
                                                    value
                                                )
                                                : 0;

                                        setEventId(
                                            id
                                        );

                                        setScopeId(
                                            0
                                        );

                                        setOverview(
                                            []
                                        );

                                        setHistoryValues(
                                            {}
                                        );

                                        setEditingHistoryKey(
                                            null
                                        );
                                    }}
                                    searchable
                                    clearable
                                />

                                <Select
                                    label={
                                        scopeType ===
                                            "function"
                                            ? "Function"
                                            : "Person"
                                    }
                                    placeholder={
                                        !eventId
                                            ? "Select event first..."
                                            : scopeType ===
                                                "function"
                                                ? "Select function..."
                                                : "Select person..."
                                    }
                                    disabled={
                                        !eventId
                                    }
                                    data={
                                        list.map(
                                            (
                                                item:
                                                    any
                                            ) => {
                                                const isFunction =
                                                    scopeType ===
                                                    "function";

                                                const personLabel =
                                                    item.fullName ||
                                                    `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() ||
                                                    `Person ${item.personId}`;

                                                return {
                                                    value:
                                                        String(
                                                            isFunction
                                                                ? item.functionId
                                                                : item.personId
                                                        ),

                                                    label:
                                                        isFunction
                                                            ? item.functionName ??
                                                            `Function ${item.functionId}`
                                                            : personLabel,
                                                };
                                            }
                                        )
                                    }
                                    value={
                                        scopeId
                                            ? String(
                                                scopeId
                                            )
                                            : null
                                    }
                                    onChange={(
                                        value
                                    ) => {
                                        setScopeId(
                                            value
                                                ? Number(
                                                    value
                                                )
                                                : 0
                                        );

                                        setOverview(
                                            []
                                        );

                                        setHistoryValues(
                                            {}
                                        );

                                        setEditingHistoryKey(
                                            null
                                        );
                                    }}
                                    searchable
                                    clearable
                                />
                            </SimpleGrid>
                        </Stack>

                        {/* ========================================= */}
                        {/* REST OF ASSIGNMENT */}
                        {/* ========================================= */}

                        {eventId > 0 &&
                            scopeId > 0 && (
                                <>
                                    <Divider />

                                    {/* PERIOD + INVOICE */}

                                    <Stack
                                        gap="md"
                                        p="lg"
                                    >
                                        <div>
                                            <Text
                                                fw={
                                                    700
                                                }
                                                size="sm"
                                            >
                                                Period & invoice
                                            </Text>

                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Choose when the allowance applies
                                            </Text>
                                        </div>

                                        <SimpleGrid
                                            cols={{
                                                base: 1,
                                                md: 3,
                                            }}
                                            spacing="lg"
                                        >
                                            <Stack
                                                gap={
                                                    6
                                                }
                                            >
                                                <Text
                                                    size="sm"
                                                    fw={
                                                        500
                                                    }
                                                >
                                                    Period
                                                </Text>

                                                <SegmentedControl
                                                    color="red"
                                                    fullWidth
                                                    value={
                                                        mode
                                                    }
                                                    onChange={(
                                                        value
                                                    ) =>
                                                        setMode(
                                                            value as
                                                            | "single"
                                                            | "range"
                                                        )
                                                    }
                                                    data={[
                                                        {
                                                            value:
                                                                "single",
                                                            label:
                                                                "Single day",
                                                        },
                                                        {
                                                            value:
                                                                "range",
                                                            label:
                                                                "Date range",
                                                        },
                                                    ]}
                                                />
                                            </Stack>

                                            {mode ===
                                                "single" ? (
                                                <DatePickerInput
                                                    label="Date"
                                                    value={
                                                        dateRangeValue[
                                                        0
                                                        ]
                                                    }
                                                    onChange={(
                                                        date
                                                    ) =>
                                                        setDateRangeValue(
                                                            [
                                                                date,
                                                                date,
                                                            ]
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <DatePickerInput
                                                    label="Date range"
                                                    type="range"
                                                    value={
                                                        dateRangeValue
                                                    }
                                                    onChange={
                                                        setDateRangeValue
                                                    }
                                                    clearable
                                                />
                                            )}

                                            <Select
                                                label="Invoice"
                                                placeholder="Select invoice..."
                                                data={
                                                    invoices
                                                        .filter(
                                                            (
                                                                invoice
                                                            ) =>
                                                                invoice.isActive
                                                        )
                                                        .map(
                                                            (
                                                                invoice
                                                            ) => ({
                                                                value:
                                                                    String(
                                                                        invoice.invoiceId
                                                                    ),

                                                                label:
                                                                    invoice.name,
                                                            })
                                                        )
                                                }
                                                value={
                                                    invoiceId
                                                        ? String(
                                                            invoiceId
                                                        )
                                                        : null
                                                }
                                                onChange={(
                                                    value
                                                ) =>
                                                    setInvoiceId(
                                                        value
                                                            ? Number(
                                                                value
                                                            )
                                                            : null
                                                    )
                                                }
                                                searchable
                                                clearable
                                            />
                                        </SimpleGrid>
                                    </Stack>

                                    <Divider />

                                    {/* ================================= */}
                                    {/* DRINKS */}
                                    {/* ================================= */}

                                    <Stack
                                        gap="md"
                                        p="lg"
                                    >
                                        <Group
                                            justify="space-between"
                                        >
                                            <div>
                                                <Text
                                                    fw={
                                                        700
                                                    }
                                                    size="sm"
                                                >
                                                    Drinks
                                                </Text>

                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    Set the maximum number of drinks for each bar
                                                </Text>
                                            </div>

                                            <Badge
                                                color="gray"
                                                variant="light"
                                            >
                                                {
                                                    bars.length
                                                }{" "}
                                                {bars.length ===
                                                    1
                                                    ? "bar"
                                                    : "bars"}
                                            </Badge>
                                        </Group>

                                        {bars.length ===
                                            0 ? (
                                            <Paper
                                                withBorder
                                                radius="md"
                                                p="xl"
                                            >
                                                <Stack
                                                    align="center"
                                                    gap="xs"
                                                >
                                                    <ThemeIcon
                                                        color="gray"
                                                        variant="light"
                                                        radius="xl"
                                                        size="lg"
                                                    >
                                                        <IconGlass
                                                            size={
                                                                18
                                                            }
                                                        />
                                                    </ThemeIcon>

                                                    <Text
                                                        fw={
                                                            600
                                                        }
                                                        size="sm"
                                                    >
                                                        No bars available
                                                    </Text>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        No bars have been configured for this event.
                                                    </Text>
                                                </Stack>
                                            </Paper>
                                        ) : (
                                            <SimpleGrid
                                                cols={{
                                                    base: 1,
                                                    sm: 2,
                                                    md: 3,
                                                }}
                                                spacing="md"
                                            >
                                                {bars.map(
                                                    (
                                                        bar
                                                    ) => (
                                                        <Paper
                                                            key={
                                                                bar.eventBarId
                                                            }
                                                            withBorder
                                                            radius="md"
                                                            p="md"
                                                        >
                                                            <Group
                                                                justify="space-between"
                                                                mb="sm"
                                                            >
                                                                <Group
                                                                    gap="xs"
                                                                >
                                                                    <ThemeIcon
                                                                        color="red"
                                                                        variant="light"
                                                                        size="sm"
                                                                    >
                                                                        <IconGlass
                                                                            size={
                                                                                13
                                                                            }
                                                                        />
                                                                    </ThemeIcon>

                                                                    <Text
                                                                        fw={
                                                                            600
                                                                        }
                                                                        size="sm"
                                                                    >
                                                                        {
                                                                            bar.name
                                                                        }
                                                                    </Text>
                                                                </Group>

                                                                <Badge
                                                                    size="xs"
                                                                    variant="light"
                                                                    color={
                                                                        bar.isActive
                                                                            ? "green"
                                                                            : "gray"
                                                                    }
                                                                >
                                                                    {bar.isActive
                                                                        ? "Active"
                                                                        : "Inactive"}
                                                                </Badge>
                                                            </Group>

                                                            <NumberInput
                                                                label="Maximum drinks"
                                                                value={
                                                                    values[
                                                                    bar
                                                                        .eventBarId
                                                                    ] ??
                                                                    0
                                                                }
                                                                min={
                                                                    0
                                                                }
                                                                onChange={(
                                                                    value
                                                                ) =>
                                                                    setValues(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,

                                                                            [bar.eventBarId]:
                                                                                Number(
                                                                                    value
                                                                                ) ||
                                                                                0,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        </Paper>
                                                    )
                                                )}
                                            </SimpleGrid>
                                        )}
                                    </Stack>

                                    {/* SAVE */}

                                    {bars.length >
                                        0 && (
                                            <>
                                                <Divider />

                                                <Group
                                                    justify="flex-end"
                                                    p="lg"
                                                >
                                                    <Button
                                                        color="red"
                                                        size="md"
                                                        loading={
                                                            saving
                                                        }
                                                        leftSection={
                                                            <IconCheck
                                                                size={
                                                                    17
                                                                }
                                                            />
                                                        }
                                                        onClick={
                                                            submit
                                                        }
                                                    >
                                                        Assign drink allowance
                                                    </Button>
                                                </Group>
                                            </>
                                        )}
                                </>
                            )}
                    </Paper>

                    {/* ============================================= */}
                    {/* EXISTING ALLOWANCES */}
                    {/* ============================================= */}

                    {eventId > 0 &&
                        scopeId > 0 && (
                            <Paper
                                withBorder
                                radius="lg"
                                shadow="xs"
                                style={{
                                    overflow:
                                        "hidden",
                                }}
                            >
                                {/* HEADER */}

                                <Group
                                    justify="space-between"
                                    p="lg"
                                >
                                    <Group gap="sm">
                                        <ThemeIcon
                                            color="red"
                                            variant="light"
                                            size="lg"
                                            radius="md"
                                        >
                                            <IconHistory
                                                size={
                                                    20
                                                }
                                            />
                                        </ThemeIcon>

                                        <div>
                                            <Title
                                                order={
                                                    4
                                                }
                                            >
                                                Existing allowances
                                            </Title>

                                            <Text
                                                size="sm"
                                                c="dimmed"
                                            >
                                                14 days previous and 14 days upcoming
                                            </Text>
                                        </div>
                                    </Group>

                                    <Badge
                                        variant="light"
                                        color="gray"
                                        size="lg"
                                    >
                                        {
                                            overview.length
                                        }{" "}
                                        {overview.length ===
                                            1
                                            ? "allowance"
                                            : "allowances"}
                                    </Badge>
                                </Group>

                                <Divider />

                                {/* EMPTY */}

                                {overview.length ===
                                    0 ? (
                                    <Stack
                                        align="center"
                                        p={
                                            50
                                        }
                                        gap="xs"
                                    >
                                        <ThemeIcon
                                            color="gray"
                                            variant="light"
                                            size={
                                                50
                                            }
                                            radius="xl"
                                        >
                                            <IconHistory
                                                size={
                                                    24
                                                }
                                            />
                                        </ThemeIcon>

                                        <Text
                                            fw={
                                                600
                                            }
                                        >
                                            No allowances found
                                        </Text>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            No drink allowances were found for this selection.
                                        </Text>
                                    </Stack>
                                ) : (
                                    <Stack
                                        gap={
                                            0
                                        }
                                    >
                                        {sortedOverviewDates.map(
                                            (
                                                date,
                                                dateIndex
                                            ) => {
                                                const items =
                                                    groupedOverview[
                                                    date
                                                    ];

                                                return (
                                                    <React.Fragment
                                                        key={
                                                            date
                                                        }
                                                    >
                                                        {/* DATE HEADER */}

                                                        <Group
                                                            p="md"
                                                            style={{
                                                                background:
                                                                    "var(--mantine-color-gray-0)",
                                                            }}
                                                        >
                                                            <ThemeIcon
                                                                color="red"
                                                                variant="light"
                                                                size="md"
                                                                radius="md"
                                                            >
                                                                <IconCalendar
                                                                    size={
                                                                        16
                                                                    }
                                                                />
                                                            </ThemeIcon>

                                                            <div>
                                                                <Text
                                                                    fw={
                                                                        700
                                                                    }
                                                                    size="sm"
                                                                >
                                                                    {new Date(
                                                                        date
                                                                    ).toLocaleDateString(
                                                                        "en-GB",
                                                                        {
                                                                            weekday:
                                                                                "long",
                                                                            day:
                                                                                "2-digit",
                                                                            month:
                                                                                "long",
                                                                            year:
                                                                                "numeric",
                                                                        }
                                                                    )}
                                                                </Text>

                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    {
                                                                        items.length
                                                                    }{" "}
                                                                    {items.length ===
                                                                        1
                                                                        ? "allowance"
                                                                        : "allowances"}
                                                                </Text>
                                                            </div>
                                                        </Group>

                                                        <Divider />

                                                        {/* DATE ITEMS */}

                                                        {items.map(
                                                            (
                                                                day:
                                                                    any,
                                                                itemIndex:
                                                                    number
                                                            ) => {
                                                                const key =
                                                                    getHistoryKey(
                                                                        day
                                                                    );

                                                                const originalValue =
                                                                    Number(
                                                                        day.maxDrinks ??
                                                                        0
                                                                    );

                                                                const currentValue =
                                                                    historyValues[
                                                                    key
                                                                    ] ??
                                                                    originalValue;

                                                                const isEditing =
                                                                    editingHistoryKey ===
                                                                    key;

                                                                const hasChanged =
                                                                    currentValue !==
                                                                    originalValue;

                                                                return (
                                                                    <React.Fragment
                                                                        key={
                                                                            key
                                                                        }
                                                                    >
                                                                        <Group
                                                                            justify="space-between"
                                                                            align="center"
                                                                            p="md"
                                                                            wrap="nowrap"
                                                                        >
                                                                            {/* INFORMATION */}

                                                                            <Group
                                                                                gap="md"
                                                                                wrap="nowrap"
                                                                            >
                                                                                <ThemeIcon
                                                                                    color="red"
                                                                                    variant="light"
                                                                                    size="lg"
                                                                                    radius="md"
                                                                                >
                                                                                    <IconGlass
                                                                                        size={
                                                                                            17
                                                                                        }
                                                                                    />
                                                                                </ThemeIcon>

                                                                                <div>
                                                                                    <Text
                                                                                        fw={
                                                                                            600
                                                                                        }
                                                                                    >
                                                                                        {day.barName ??
                                                                                            `Bar ${day.eventBarId ??
                                                                                            day.barId ??
                                                                                            day.bar
                                                                                            }`}
                                                                                    </Text>

                                                                                    <Group
                                                                                        gap={
                                                                                            6
                                                                                        }
                                                                                        mt={
                                                                                            3
                                                                                        }
                                                                                    >
                                                                                        {day.invoiceName && (
                                                                                            <>
                                                                                                <Text
                                                                                                    size="sm"
                                                                                                    c="dimmed"
                                                                                                >
                                                                                                    {
                                                                                                        day.invoiceName
                                                                                                    }
                                                                                                </Text>

                                                                                                <Text
                                                                                                    size="sm"
                                                                                                    c="dimmed"
                                                                                                >
                                                                                                    •
                                                                                                </Text>
                                                                                            </>
                                                                                        )}

                                                                                        <Text
                                                                                            size="sm"
                                                                                            c="dimmed"
                                                                                        >
                                                                                            {
                                                                                                originalValue
                                                                                            }{" "}
                                                                                            drinks
                                                                                        </Text>

                                                                                        <Text
                                                                                            size="sm"
                                                                                            c="dimmed"
                                                                                        >
                                                                                            •
                                                                                        </Text>

                                                                                        <Text
                                                                                            size="sm"
                                                                                            c="dimmed"
                                                                                        >
                                                                                            {day.ticketsLeft ??
                                                                                                0}{" "}
                                                                                            remaining
                                                                                        </Text>
                                                                                    </Group>
                                                                                </div>
                                                                            </Group>

                                                                            {/* ACTION */}

                                                                            {!isEditing ? (
                                                                                <Button
                                                                                    variant="light"
                                                                                    color="gray"
                                                                                    leftSection={
                                                                                        <IconEdit
                                                                                            size={
                                                                                                15
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    onClick={() => {
                                                                                        setHistoryValues(
                                                                                            (
                                                                                                prev
                                                                                            ) => ({
                                                                                                ...prev,

                                                                                                [key]:
                                                                                                    originalValue,
                                                                                            })
                                                                                        );

                                                                                        setEditingHistoryKey(
                                                                                            key
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    Edit
                                                                                </Button>
                                                                            ) : (
                                                                                <Group
                                                                                    gap="sm"
                                                                                    wrap="nowrap"
                                                                                >
                                                                                    <NumberInput
                                                                                        value={
                                                                                            currentValue
                                                                                        }
                                                                                        min={
                                                                                            0
                                                                                        }
                                                                                        onChange={(
                                                                                            value
                                                                                        ) =>
                                                                                            setHistoryValues(
                                                                                                (
                                                                                                    prev
                                                                                                ) => ({
                                                                                                    ...prev,

                                                                                                    [key]:
                                                                                                        Number(
                                                                                                            value
                                                                                                        ) ||
                                                                                                        0,
                                                                                                })
                                                                                            )
                                                                                        }
                                                                                        style={{
                                                                                            width:
                                                                                                110,
                                                                                        }}
                                                                                    />

                                                                                    <Button
                                                                                        variant="default"
                                                                                        disabled={
                                                                                            savingHistoryKey !==
                                                                                            null
                                                                                        }
                                                                                        onClick={() => {
                                                                                            setEditingHistoryKey(
                                                                                                null
                                                                                            );

                                                                                            setHistoryValues(
                                                                                                (
                                                                                                    prev
                                                                                                ) => {
                                                                                                    const next =
                                                                                                    {
                                                                                                        ...prev,
                                                                                                    };

                                                                                                    delete next[
                                                                                                        key
                                                                                                    ];

                                                                                                    return next;
                                                                                                }
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        Cancel
                                                                                    </Button>

                                                                                    <Button
                                                                                        color="red"
                                                                                        loading={
                                                                                            savingHistoryKey ===
                                                                                            key
                                                                                        }
                                                                                        disabled={
                                                                                            savingHistoryKey !==
                                                                                            null ||
                                                                                            !hasChanged
                                                                                        }
                                                                                        onClick={async () => {
                                                                                            const success =
                                                                                                await updateHistoryAllowance(
                                                                                                    day,
                                                                                                    currentValue
                                                                                                );

                                                                                            if (
                                                                                                success
                                                                                            ) {
                                                                                                setEditingHistoryKey(
                                                                                                    null
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        Save
                                                                                    </Button>
                                                                                </Group>
                                                                            )}
                                                                        </Group>

                                                                        {itemIndex <
                                                                            items.length -
                                                                            1 && (
                                                                                <Divider />
                                                                            )}
                                                                    </React.Fragment>
                                                                );
                                                            }
                                                        )}

                                                        {dateIndex <
                                                            sortedOverviewDates.length -
                                                            1 && (
                                                                <Divider
                                                                    size="md"
                                                                />
                                                            )}
                                                    </React.Fragment>
                                                );
                                            }
                                        )}
                                    </Stack>
                                )}
                            </Paper>
                        )}
                </Stack>
            </Container>
        </div>
    );
};

export default AdminDrinkAllowance;